import type { ReasonSelection } from "~/helpers/mobile/stickyReason";
import type { ContinuationCreatedRecord } from "~/store/pendingIntentSlice";
import type { ScheduledTransferValues } from "~fulfill/inventory/stock/helpers/scheduledInventoryTransfers";
import type { InventorySegment } from "~fulfill/inventory/stock/mobile/helpers/inventoryCardFields";

import { ScheduledTransferQuantityMode } from "~/gql/graphqlBase.generated";
import isInventoryCorrectionDraft, {
	type InventoryCorrectionDraft
} from "~fulfill/inventory/stock/helpers/inventoryCorrectionDraft";
import isInventoryReconcileDraft, {
	type InventoryReconcileDraft
} from "~fulfill/inventory/stock/helpers/inventoryReconcileDraft";
import { NEW_SALES_ITEM_TARGET_KIND } from "~produce/production/helpers/productionSetupReceiverIdentity";

export const INVENTORY_SOURCE_KIND = "inventory";

export const INVENTORY_BASE_PATH = "/fulfill/inventory";

export const INVENTORY_TAB_ROUTES: Record<InventorySegment, string> = {
	availability: `${INVENTORY_BASE_PATH}/availability`,
	"hard-goods": `${INVENTORY_BASE_PATH}/hard-goods`,
	"live-goods": INVENTORY_BASE_PATH,
	"scheduled-transfers": `${INVENTORY_BASE_PATH}/scheduled-transfers`
};

export const isInventoryRoute = (pathname: string): boolean =>
	pathname === INVENTORY_BASE_PATH || pathname.startsWith(`${INVENTORY_BASE_PATH}/`);

export interface InventoryTransferTarget {
	itemId: string;
	name: string;
	sku: string;
}

export interface InventoryTransferDraft {
	batchCode?: null | string;
	quantity: null | number;
	reason: null | ReasonSelection;
	schedule?: null | ScheduledTransferValues;
	showAllItems: boolean;
	sourceAvailable: number;
	toGrade: string;
	toItem: InventoryTransferTarget | null;
	toLocation: { id: string; name: string } | null;
	why: string;
	whyEdited: boolean;
}

export interface InventoryAddLotItem {
	batchTracked?: boolean;
	effectiveEachesPerUnit: null | number;
	effectiveShippingUomId: null | string;
	itemId: string;
	name: string;
	sku: string;
}

export interface InventoryAddLotDraft {
	batchCode?: null | string;
	grade: string;
	isHardGoods: boolean;
	item: InventoryAddLotItem | null;
	location: { id: string; name: string } | null;
	plantDate?: null | string;
	plantStatus?: string;
	quantity: null | number;
	readyDate: null | string;
	reason: null | ReasonSelection;
	siteLocationId: null | string;
	why: string;
}

export interface InventorySourceCheckpoint {
	addLotDraft?: InventoryAddLotDraft;
	correctionDraft?: InventoryCorrectionDraft;
	inventoryItemId: null | string;
	itemName: string;
	reconcileDraft?: InventoryReconcileDraft;
	searchText: string;
	transferDraft?: InventoryTransferDraft;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

const isNullableNumber = (value: unknown): boolean => value === null || typeof value === "number";

const isReason = (value: unknown): boolean =>
	value === null || (isRecord(value) && typeof value.code === "number" && typeof value.text === "string");

const isTransferTarget = (value: unknown): boolean =>
	value === null ||
	(isRecord(value) &&
		typeof value.itemId === "string" &&
		typeof value.name === "string" &&
		typeof value.sku === "string");

const isPickedLocation = (value: unknown): boolean =>
	value === null || (isRecord(value) && typeof value.id === "string" && typeof value.name === "string");

const SCHEDULE_QUANTITY_MODES: readonly unknown[] = [
	ScheduledTransferQuantityMode.Fixed,
	ScheduledTransferQuantityMode.Remaining
];

const isTransferSchedule = (value: unknown): boolean =>
	value === undefined ||
	value === null ||
	(isRecord(value) &&
		(value.effectiveDate === null || typeof value.effectiveDate === "string") &&
		isNullableNumber(value.fixedQuantity) &&
		SCHEDULE_QUANTITY_MODES.includes(value.quantityMode));

export const isInventoryTransferDraft = (value: unknown): value is InventoryTransferDraft =>
	isRecord(value) &&
	(value.batchCode === undefined || value.batchCode === null || typeof value.batchCode === "string") &&
	isNullableNumber(value.quantity) &&
	isReason(value.reason) &&
	isTransferSchedule(value.schedule) &&
	typeof value.showAllItems === "boolean" &&
	typeof value.sourceAvailable === "number" &&
	typeof value.toGrade === "string" &&
	isTransferTarget(value.toItem) &&
	isPickedLocation(value.toLocation) &&
	typeof value.why === "string" &&
	typeof value.whyEdited === "boolean";

const isNullableString = (value: unknown): boolean => value === null || typeof value === "string";

const isAddLotItem = (value: unknown): boolean =>
	value === null ||
	(isRecord(value) &&
		(value.batchTracked === undefined || typeof value.batchTracked === "boolean") &&
		isNullableNumber(value.effectiveEachesPerUnit) &&
		isNullableString(value.effectiveShippingUomId) &&
		typeof value.itemId === "string" &&
		typeof value.name === "string" &&
		typeof value.sku === "string");

export const isInventoryAddLotDraft = (value: unknown): value is InventoryAddLotDraft =>
	isRecord(value) &&
	(value.batchCode === undefined || isNullableString(value.batchCode)) &&
	typeof value.grade === "string" &&
	typeof value.isHardGoods === "boolean" &&
	isAddLotItem(value.item) &&
	isPickedLocation(value.location) &&
	(value.plantDate === undefined || isNullableString(value.plantDate)) &&
	(value.plantStatus === undefined || typeof value.plantStatus === "string") &&
	isNullableNumber(value.quantity) &&
	isNullableString(value.readyDate) &&
	isReason(value.reason) &&
	isNullableString(value.siteLocationId) &&
	typeof value.why === "string";

export const isInventorySourceCheckpoint = (value: unknown): value is InventorySourceCheckpoint =>
	isRecord(value) &&
	(value.addLotDraft === undefined || isInventoryAddLotDraft(value.addLotDraft)) &&
	isNullableString(value.inventoryItemId) &&
	typeof value.itemName === "string" &&
	typeof value.searchText === "string" &&
	(value.transferDraft === undefined || isInventoryTransferDraft(value.transferDraft)) &&
	(value.correctionDraft === undefined || isInventoryCorrectionDraft(value.correctionDraft)) &&
	(value.reconcileDraft === undefined || isInventoryReconcileDraft(value.reconcileDraft));

export const seedCreatedTransferTarget = (
	draft: InventoryTransferDraft,
	createdRecord: ContinuationCreatedRecord | undefined
): InventoryTransferDraft =>
	createdRecord?.targetKind === NEW_SALES_ITEM_TARGET_KIND
		? { ...draft, toItem: { itemId: createdRecord.id, name: "", sku: "" } }
		: draft;

export const seedCreatedAddLotItem = (
	draft: InventoryAddLotDraft,
	createdRecord: ContinuationCreatedRecord | undefined
): InventoryAddLotDraft =>
	createdRecord?.targetKind === NEW_SALES_ITEM_TARGET_KIND && !draft.isHardGoods
		? {
				...draft,
				batchCode: null,
				item: {
					effectiveEachesPerUnit: null,
					effectiveShippingUomId: null,
					itemId: createdRecord.id,
					name: "",
					sku: ""
				},
				plantDate: null,
				plantStatus: ""
			}
		: draft;

export const ADD_LOT_EDITOR_ID = "addLot";

export const RECONCILE_EDITOR_ID = "reconcile";

const inventoryEditorKey = (inventoryItemId: string): string => `inventory:${inventoryItemId}`;

export default inventoryEditorKey;
